/**
 * File: saga.js
 * Project: Aitea-Middle
 * File Created: Thursday, 25th April 2024 10:56:45 am
 * Author: Jorge Sierra Blázquez (jorge.sierra@aerin.es)
 * Version: 1.0.0
 * -----
 * Last Modified: Thursday, 25th April 2024 10:58:21 am
 * Modified By: Jorge Sierra Blázquez
 * -----
 * Copyright (c) 2023 - 2024 Aerin Sistemas S. L. copying, distribution or modification not authorised in writing is prohibited.
 */

import { all, call, fork, put, takeEvery } from 'redux-saga/effects';
import axios from 'axios';

import {
  LOGIN_USER,
  LOAD_USER,
  REGISTER_USER,
  LOGOUT_USER,
  FORGOT_PASSWORD,
  RESET_PASSWORD,
} from '../contants';

import {
  loginUserSuccess,
  loginUserError,
  loadUserSuccess,
  loadUserError,
  registerUserSuccess,
  registerUserError,
  forgotPasswordSuccess,
  forgotPasswordError,
  resetPasswordSuccess,
  resetPasswordError,
} from './actions';

import { adminRoot, loginRoot } from '../../constants/defaultValues';
import { setCurrentUser, setAuthToken } from '../../helpers/Utils';

export function* watchLoginUser() {
  // eslint-disable-next-line no-use-before-define
  yield takeEvery(LOGIN_USER, loginWithUsernamePassword);
}

const loginWithUsernamePasswordAsync = async (username, password) => {
  // eslint-disable-next-line no-return-await
  const { REACT_APP_URL_LOGIN } = process.env;
  return axios
    .post(
      REACT_APP_URL_LOGIN,
      {
        username,
        password,
      }
      // {
      //   headers: {
      //     'Content-Type': 'application/json',
      //   }
      // }
    )
    .then((user) => user)
    .catch((error) => error);
};

function* loginWithUsernamePassword({ payload }) {
  const { username, password } = payload.user;
  const { history } = payload;

  if (
    typeof axios.defaults.headers.common === 'object' &&
    'Authorization' in axios.defaults.headers.common
  ) {
    delete axios.defaults.headers.common.Authorization;
  }

  try {
    const loginUser = yield call(
      loginWithUsernamePasswordAsync,
      username,
      password
    );
    if (!loginUser.message) {
      setCurrentUser(loginUser.data.user);
      setAuthToken(loginUser.data.token, loginUser.data.user.is_superuser);
      axios.defaults.headers.common.Authorization = `Token ${loginUser.data.token}`;
      yield put(loginUserSuccess(loginUser.data));
      history.push(adminRoot);
    } else {
      const message = 'Check your credentials.';
      yield put(loginUserError(message));
    }
  } catch (error) {
    yield put(loginUserError(error));
  }
}

export function* watchLoadUser() {
  // eslint-disable-next-line no-use-before-define
  yield takeEvery(LOAD_USER, loadUser);
}

const loadUserAsync = async () =>
  // eslint-disable-next-line no-return-await
  axios
    .get('/api/auth/user/')
    .then((user) => user)
    .catch((error) => error);

function* loadUser({ payload }) {
  const { history } = payload;
  try {
    const loginUser = yield call(loadUserAsync);
    if (!loginUser.message) {
      yield put(loadUserSuccess(loginUser.data));
    } else {
      const message = 'Expired session token.';
      yield put(loadUserError(message));
      history.push(loginRoot);
    }
  } catch (error) {
    yield put(loadUserError(error));
    history.push(loginRoot);
  }
}

export function* watchRegisterUser() {
  // eslint-disable-next-line no-use-before-define
  yield takeEvery(REGISTER_USER, registerWithEmailPassword);
}

const registerWithEmailPasswordAsync = async () => {
  // eslint-disable-next-line no-return-await
  /*
  await auth
    .createUserWithEmailAndPassword(email, password)
    .then((user) => user)
    .catch((error) => error);
  */
};

function* registerWithEmailPassword() {
  const { email, password } = '';
  const { history } = '';
  try {
    const registerUser = yield call(
      registerWithEmailPasswordAsync,
      email,
      password
    );
    if (!registerUser.message) {
      const item = { uid: registerUser.user.uid };
      setCurrentUser(item);
      yield put(registerUserSuccess(item));
      history.push(adminRoot);
    } else {
      yield put(registerUserError(registerUser.message));
    }
  } catch (error) {
    yield put(registerUserError(error));
  }
}

export function* watchLogoutUser() {
  // eslint-disable-next-line no-use-before-define
  yield takeEvery(LOGOUT_USER, logout);
}

const logoutAsync = async () => {
  const { REACT_APP_URL_LOGOUT } = process.env;
  return axios
    .post(REACT_APP_URL_LOGOUT, {})
    .then((response) => response)
    .catch((error) => error);
};

// eslint-disable-next-line no-unused-vars
function* logout({ payload }) {
  // const { history } = payload;
  try {
    yield call(logoutAsync);
  } catch (error) {
    // El console log está para evitar dejar el catch vacío.
    console.log('Logout error.');
  } finally {
    setCurrentUser(null);
    setAuthToken(null, null);
    if (
      typeof axios.defaults.headers.common === 'object' &&
      'Authorization' in axios.defaults.headers.common
    ) {
      delete axios.defaults.headers.common.Authorization;
    }
    // history.push(loginRoot);
    window.location.href = loginRoot;
  }
}

export function* watchForgotPassword() {
  // eslint-disable-next-line no-use-before-define
  yield takeEvery(FORGOT_PASSWORD, forgotPassword);
}

const forgotPasswordAsync = async () => {
  // eslint-disable-next-line no-return-await
  /*
  return await auth
    .sendPasswordResetEmail(email)
    .then((user) => user)
    .catch((error) => error);
  */
};

function* forgotPassword({ payload }) {
  const { email } = payload.forgotUserMail;
  try {
    const forgotPasswordStatus = yield call(forgotPasswordAsync, email);
    if (!forgotPasswordStatus) {
      yield put(forgotPasswordSuccess('success'));
    } else {
      yield put(forgotPasswordError(forgotPasswordStatus.message));
    }
  } catch (error) {
    yield put(forgotPasswordError(error));
  }
}

export function* watchResetPassword() {
  // eslint-disable-next-line no-use-before-define
  yield takeEvery(RESET_PASSWORD, resetPassword);
}

const resetPasswordAsync = async () => {
  // eslint-disable-next-line no-return-await
  /*
  return await auth
    .confirmPasswordReset(resetPasswordCode, newPassword)
    .then((user) => user)
    .catch((error) => error);
  */
};

function* resetPassword({ payload }) {
  const { newPassword, resetPasswordCode } = payload;
  try {
    const resetPasswordStatus = yield call(
      resetPasswordAsync,
      resetPasswordCode,
      newPassword
    );
    if (!resetPasswordStatus) {
      yield put(resetPasswordSuccess('success'));
    } else {
      yield put(resetPasswordError(resetPasswordStatus.message));
    }
  } catch (error) {
    yield put(resetPasswordError(error));
  }
}

export default function* rootSaga() {
  yield all([
    fork(watchLoginUser),
    fork(watchLogoutUser),
    fork(watchLoadUser),
    fork(watchRegisterUser),
    fork(watchForgotPassword),
    fork(watchResetPassword),
  ]);
}
